import React, { useState, useEffect } from 'react';
import { vacanciesAPI } from '../services/api';
import './Vacancies.css';

interface Vacancy {
  id: number;
  title: string;
  company: string;
  location: string;
  type: string;
  experience: string;
  salary: string;
  description: string;
  requirements: string[];
  responsibilities: string[];
  applicationUrl: string;
  postedDate: string;
  deadline: string;
}

const Vacancies: React.FC = () => {
  const [vacancies, setVacancies] = useState<Vacancy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVacancy, setSelectedVacancy] = useState<Vacancy | null>(null);

  useEffect(() => {
    fetchVacancies();
  }, []);

  const fetchVacancies = async () => {
    try {
      setLoading(true);
      const response = await vacanciesAPI.getAll();
      if (response.data.success) {
        setVacancies(response.data.data);
      } else {
        setError('Failed to fetch vacancies');
      }
    } catch (err) {
      setError('Error loading vacancies. Please try again later.');
      console.error('Error fetching vacancies:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      fetchVacancies();
      return;
    }

    try {
      setLoading(true);
      const response = await vacanciesAPI.search(searchTerm);
      if (response.data.success) {
        setVacancies(response.data.data);
      }
    } catch (err) {
      console.error('Error searching vacancies:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApply = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const filteredVacancies = vacancies.filter(vacancy =>
    vacancy.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    vacancy.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
    vacancy.location.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="vacancies-page">
        <div className="container">
          <div className="loading">
            <div className="spinner"></div>
            <p>Loading job opportunities...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="vacancies-page">
        <div className="container">
          <div className="error">
            <h2>Error Loading Vacancies</h2>
            <p>{error}</p>
            <button onClick={fetchVacancies} className="btn btn-primary">
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="vacancies-page">
      <div className="container">
        {/* Header */}
        <div className="vacancies-header">
          <h1>Job Opportunities</h1>
          <p>Discover exciting career opportunities in Guyana's oil and gas industry</p>
        </div>

        {/* Search Section */}
        <div className="search-section">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search jobs by title, company, or location..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
            <button onClick={handleSearch} className="btn btn-primary">
              Search
            </button>
            <button onClick={fetchVacancies} className="btn btn-secondary">
              Refresh Jobs
            </button>
          </div>
        </div>

        {/* Vacancies Grid */}
        <div className="vacancies-grid">
          {filteredVacancies.map((vacancy) => (
            <div key={vacancy.id} className="vacancy-card">
              <div className="vacancy-header">
                <h3>{vacancy.title}</h3>
                <span className="company">{vacancy.company}</span>
              </div>
              
              <div className="vacancy-details">
                <div className="detail-item">
                  <span className="label">📍 Location:</span>
                  <span>{vacancy.location}</span>
                </div>
                <div className="detail-item">
                  <span className="label">💼 Type:</span>
                  <span>{vacancy.type}</span>
                </div>
                <div className="detail-item">
                  <span className="label">⏰ Experience:</span>
                  <span>{vacancy.experience}</span>
                </div>
                <div className="detail-item">
                  <span className="label">💰 Salary:</span>
                  <span>{vacancy.salary}</span>
                </div>
              </div>

              <p className="vacancy-description">{vacancy.description}</p>

              <div className="vacancy-meta">
                <span className="posted-date">Posted: {formatDate(vacancy.postedDate)}</span>
                <span className="deadline">Deadline: {formatDate(vacancy.deadline)}</span>
              </div>

              <div className="vacancy-actions">
                <button
                  onClick={() => setSelectedVacancy(vacancy)}
                  className="btn btn-outline"
                >
                  View Details
                </button>
                <button
                  onClick={() => handleApply(vacancy.applicationUrl)}
                  className="btn btn-primary"
                >
                  Apply Now
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* No Results */}
        {filteredVacancies.length === 0 && !loading && (
          <div className="no-vacancies">
            <h3>No Jobs Found</h3>
            <p>Try adjusting your search criteria or check back later for new opportunities.</p>
          </div>
        )}

        {/* Vacancy Modal */}
        {selectedVacancy && (
          <div className="modal-overlay" onClick={() => setSelectedVacancy(null)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{selectedVacancy.title}</h2>
                <button 
                  className="modal-close"
                  onClick={() => setSelectedVacancy(null)}
                >
                  ×
                </button>
              </div>
              
              <div className="modal-body">
                <div className="modal-details">
                  <p><strong>Company:</strong> {selectedVacancy.company}</p>
                  <p><strong>Location:</strong> {selectedVacancy.location}</p>
                  <p><strong>Type:</strong> {selectedVacancy.type}</p>
                  <p><strong>Experience:</strong> {selectedVacancy.experience}</p>
                  <p><strong>Salary:</strong> {selectedVacancy.salary}</p>
                </div>

                <div className="modal-section">
                  <h3>Description</h3>
                  <p>{selectedVacancy.description}</p>
                </div>

                <div className="modal-section">
                  <h3>Requirements</h3>
                  <ul>
                    {selectedVacancy.requirements.map((req, index) => (
                      <li key={index}>{req}</li>
                    ))}
                  </ul>
                </div>

                <div className="modal-section">
                  <h3>Responsibilities</h3>
                  <ul>
                    {selectedVacancy.responsibilities.map((resp, index) => (
                      <li key={index}>{resp}</li>
                    ))}
                  </ul>
                </div>

                <div className="modal-actions">
                  <button
                    onClick={() => handleApply(selectedVacancy.applicationUrl)}
                    className="btn btn-primary"
                  >
                    Apply for this Position
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Vacancies;
